import { useRef, useState } from "react";
import { Upload, Loader2, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { assetUrl } from "@/lib/siteContent";

export const ImageField = ({ id, label, value, onChange, optional = false, onBusy }) => {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setError("Choose a JPG, PNG, WebP, or GIF under 10 MB."); return;
    }
    setUploading(true); onBusy?.(true); setError("");
    try {
      const data = new FormData(); data.append("file", file);
      const out = await adminApi("/upload", { method: "POST", body: data });
      onChange(out.url); toast.success("Photo uploaded.");
    } catch (e) { setError(e.message); }
    finally { setUploading(false); onBusy?.(false); }
  };
  return <div className="space-y-3 min-w-0" data-testid={`${id}-field`}>
    <p className="text-sm text-slate-300 font-medium">{label}</p>
    <div data-testid={`${id}-drop-zone`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!uploading) upload(e.dataTransfer.files?.[0]); }} className="border border-dashed border-slate-600 rounded-md bg-navy-light aspect-[16/7] flex items-center justify-center overflow-hidden">
      {value ? <img src={assetUrl(value)} alt={label} data-testid={`${id}-preview`} className="w-full h-full object-contain" /> : <ImagePlus className="text-slate-500 w-9 h-9" />}
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid={`${id}-file`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <Button type="button" variant="outline" disabled={uploading} data-testid={`${id}-upload`} onClick={() => input.current?.click()}>{uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}{value ? "Replace photo" : "Upload photo"}</Button>
      {optional && value && <Button type="button" variant="ghost" disabled={uploading} data-testid={`${id}-remove`} onClick={() => onChange("")}>Remove</Button>}
      <span className="text-xs text-slate-500">JPG, PNG, WebP, GIF · 10 MB max</span>
    </div>
    {error && <p role="alert" data-testid={`${id}-error`} className="text-sm text-red-400">{error}</p>}
  </div>;
};